/**
 * Canvas rendering of the 2026-11-04 border styles (2026-11-04).
 *
 * BorderlineEngine draws the picked border as an SVG overlay on
 * the screen preview. The pre-existing styles never enter the
 * fabric canvas, so they do not appear in canvas exports — that is
 * the established behaviour for the old styles and is NOT changed
 * here. The five styles added in the design-library expansion are
 * drawn BOTH ways: the SVG overlay for the preview and this canvas
 * pass (called from CheckBuilder's renderCheck onto the same
 * offscreen layer that becomes the canvas background) so the design
 * a user picks is the design the export carries. The geometry here
 * mirrors the SVG renderers in BorderlineEngine.tsx — same insets,
 * same band widths, same motifs — drawn in the border's own colours.
 */

export const NEW_BORDER_STYLES = ['ROPE', 'DOUBLE_RULE', 'GREEK_KEY', 'DIAMOND_CHAIN', 'BEAD', 'MICROPRINT_FRAME', 'DOUBLE_RULE_BOLD'] as const;
export type NewBorderStyle = typeof NEW_BORDER_STYLES[number];

export function isNewBorderStyle(style: string | null | undefined): style is NewBorderStyle {
    return !!style && (NEW_BORDER_STYLES as readonly string[]).includes(style);
}

export interface CanvasBorderConfig {
    style: string;
    color: string;
    secondaryColor?: string;
    thickness: number;
    cornerRadius: number;
}

type Ctx = CanvasRenderingContext2D;

/** Run `fn` along each of the four frame edges (inset c from the edge). */
function eachEdge(ctx: Ctx, W: number, H: number, inset: number, fn: (x1: number, y1: number, x2: number, y2: number) => void) {
    fn(inset, inset, W - inset, inset);           // top
    fn(W - inset, inset, W - inset, H - inset);   // right
    fn(W - inset, H - inset, inset, H - inset);   // bottom
    fn(inset, H - inset, inset, inset);           // left
}

/** Stroke a sine strand along an edge: fn-space is (along, across). */
function strandAlong(ctx: Ctx, x1: number, y1: number, x2: number, y2: number, amp: number, phase: number, wavelength: number) {
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    const ang = Math.atan2(dy, dx);
    ctx.save();
    ctx.translate(x1, y1);
    ctx.rotate(ang);
    ctx.beginPath();
    for (let t = 0; t <= len; t += 3) {
        const y = Math.sin((t / wavelength) * Math.PI * 2 + phase) * amp;
        if (t === 0) ctx.moveTo(t, y); else ctx.lineTo(t, y);
    }
    ctx.stroke();
    ctx.restore();
}

const drawRope = (ctx: Ctx, W: number, H: number, cfg: CanvasBorderConfig) => {
    const m = Math.max(3, cfg.thickness);
    const color = cfg.color, secondary = cfg.secondaryColor || color;
    // Outer + inner rails
    ctx.strokeStyle = color; ctx.lineWidth = 1;
    ctx.strokeRect(1, 1, W - 2, H - 2);
    const inner = m * 3.5;
    ctx.strokeStyle = secondary; ctx.lineWidth = 0.6; ctx.globalAlpha = 0.55;
    ctx.strokeRect(inner, inner, W - inner * 2, H - inner * 2);
    ctx.globalAlpha = 1;
    // Two twisted strands riding the band centreline (inset m*1.75),
    // with diagonal ticks between crossings to read as rope lay.
    const c = m * 1.75;
    const amp = m * 0.9;
    ctx.lineWidth = Math.max(0.8, m * 0.42);
    eachEdge(ctx, W, H, c, (x1, y1, x2, y2) => {
        ctx.strokeStyle = color;
        strandAlong(ctx, x1, y1, x2, y2, amp, 0, m * 5);
        ctx.strokeStyle = secondary; ctx.globalAlpha = 0.75;
        strandAlong(ctx, x1, y1, x2, y2, amp, Math.PI, m * 5);
        ctx.globalAlpha = 1;
    });
};

const drawDoubleRule = (ctx: Ctx, W: number, H: number, cfg: CanvasBorderConfig) => {
    const m = Math.max(3, cfg.thickness);
    const color = cfg.color, secondary = cfg.secondaryColor || color;
    const r = cfg.cornerRadius;
    // Heavy outer rule
    ctx.strokeStyle = color; ctx.lineWidth = m;
    ctx.strokeRect(2 + m / 2, 2 + m / 2, W - 4 - m, H - 4 - m);
    // Close hairline inside it (the classic double rule pairing)
    ctx.strokeStyle = color; ctx.lineWidth = 0.7;
    const h1 = 4 + m + 2.5;
    ctx.strokeRect(h1, h1, W - h1 * 2, H - h1 * 2);
    // Secondary hairline and corner blocks
    ctx.strokeStyle = secondary; ctx.lineWidth = 0.45; ctx.globalAlpha = 0.6;
    const h2 = h1 + 3;
    ctx.strokeRect(h2, h2, W - h2 * 2, H - h2 * 2);
    ctx.globalAlpha = 1;
    ctx.fillStyle = color;
    const b = Math.max(4, m * 1.4);
    for (const [cx, cy] of [[h1, h1], [W - h1, h1], [W - h1, H - h1], [h1, H - h1]]) {
        ctx.fillRect(cx - b / 2, cy - b / 2, b, b);
    }
    void r;
};

const drawGreekKey = (ctx: Ctx, W: number, H: number, cfg: CanvasBorderConfig) => {
    const m = Math.max(3, cfg.thickness);
    const color = cfg.color, secondary = cfg.secondaryColor || color;
    ctx.strokeStyle = color; ctx.lineWidth = 1.4;
    ctx.strokeRect(2, 2, W - 4, H - 4);
    const unit = Math.max(10, m * 4);
    const bandTop = 6;
    ctx.strokeStyle = color; ctx.lineWidth = Math.max(0.8, m * 0.4);
    // One meander cell in local space: a stepped spiral segment.
    const cell = (ox: number, oy: number, rot: number) => {
        ctx.save();
        ctx.translate(ox, oy);
        ctx.rotate(rot);
        const u = unit;
        ctx.beginPath();
        ctx.moveTo(0, u * 0.82);
        ctx.lineTo(0, 0);
        ctx.lineTo(u * 0.82, 0);
        ctx.lineTo(u * 0.82, u * 0.62);
        ctx.lineTo(u * 0.3, u * 0.62);
        ctx.lineTo(u * 0.3, u * 0.3);
        ctx.lineTo(u * 0.58, u * 0.3);
        ctx.stroke();
        ctx.restore();
    };
    for (let x = bandTop; x + unit <= W - bandTop; x += unit) cell(x, bandTop, 0);
    for (let x = bandTop; x + unit <= W - bandTop; x += unit) {
        ctx.save(); ctx.translate(x + unit, H - bandTop); ctx.rotate(Math.PI); cell(0, 0, 0); ctx.restore();
    }
    for (let y = bandTop; y + unit <= H - bandTop; y += unit) {
        ctx.save(); ctx.translate(bandTop, y + unit); ctx.rotate(-Math.PI / 2); cell(0, 0, 0); ctx.restore();
    }
    for (let y = bandTop; y + unit <= H - bandTop; y += unit) {
        ctx.save(); ctx.translate(W - bandTop, y); ctx.rotate(Math.PI / 2); cell(0, 0, 0); ctx.restore();
    }
    const inner = bandTop + unit + 3;
    ctx.strokeStyle = secondary; ctx.lineWidth = 0.7; ctx.globalAlpha = 0.7;
    ctx.strokeRect(inner, inner, W - inner * 2, H - inner * 2);
    ctx.globalAlpha = 1;
};

const drawDiamondChain = (ctx: Ctx, W: number, H: number, cfg: CanvasBorderConfig) => {
    const m = Math.max(3, cfg.thickness);
    const color = cfg.color, secondary = cfg.secondaryColor || color;
    ctx.strokeStyle = color; ctx.lineWidth = 1;
    ctx.strokeRect(2, 2, W - 4, H - 4);
    const c = 6 + m * 2.2; // chain centreline inset
    const half = Math.max(4, m * 1.5);
    const spacing = half * 4;
    ctx.lineWidth = Math.max(0.8, m * 0.38);
    const diamond = (cx: number, cy: number, tone: string) => {
        ctx.strokeStyle = tone;
        ctx.beginPath();
        ctx.moveTo(cx, cy - half); ctx.lineTo(cx + half, cy); ctx.lineTo(cx, cy + half); ctx.lineTo(cx - half, cy);
        ctx.closePath(); ctx.stroke();
        ctx.fillStyle = tone;
        ctx.beginPath(); ctx.arc(cx, cy, 1.1, 0, Math.PI * 2); ctx.fill();
    };
    const dot = (cx: number, cy: number) => {
        ctx.fillStyle = secondary;
        ctx.beginPath(); ctx.arc(cx, cy, 1.2, 0, Math.PI * 2); ctx.fill();
    };
    for (let x = c, i = 0; x <= W - c; x += spacing, i++) {
        diamond(x, c, i % 2 ? secondary : color);
        diamond(x, H - c, i % 2 ? color : secondary);
        if (x + spacing / 2 <= W - c) { dot(x + spacing / 2, c); dot(x + spacing / 2, H - c); }
    }
    for (let y = c + spacing, i = 1; y <= H - c; y += spacing, i++) {
        diamond(c, y, i % 2 ? secondary : color);
        diamond(W - c, y, i % 2 ? color : secondary);
        if (y + spacing / 2 <= H - c) { dot(c, y + spacing / 2); dot(W - c, y + spacing / 2); }
    }
    const inner = c + half + 3;
    ctx.strokeStyle = secondary; ctx.lineWidth = 0.6; ctx.globalAlpha = 0.6;
    ctx.strokeRect(inner, inner, W - inner * 2, H - inner * 2);
    ctx.globalAlpha = 1;
};

const drawBead = (ctx: Ctx, W: number, H: number, cfg: CanvasBorderConfig) => {
    const m = Math.max(3, cfg.thickness);
    const color = cfg.color, secondary = cfg.secondaryColor || color;
    ctx.strokeStyle = color; ctx.lineWidth = 0.9;
    ctx.strokeRect(3, 3, W - 6, H - 6);
    const c = 6 + m * 1.6;
    const r = Math.max(1.7, m * 0.62);
    const spacing = r * 2 + 2.6;
    const bead = (cx: number, cy: number, filled: boolean, tone: string) => {
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
        if (filled) { ctx.fillStyle = tone; ctx.fill(); }
        else { ctx.strokeStyle = tone; ctx.lineWidth = 0.7; ctx.stroke(); }
    };
    for (let x = c, i = 0; x <= W - c; x += spacing, i++) {
        bead(x, c, i % 2 === 0, i % 2 === 0 ? color : secondary);
        bead(x, H - c, i % 2 === 0, i % 2 === 0 ? color : secondary);
    }
    for (let y = c + spacing, i = 1; y <= H - c; y += spacing, i++) {
        bead(c, y, i % 2 === 0, i % 2 === 0 ? color : secondary);
        bead(W - c, y, i % 2 === 0, i % 2 === 0 ? color : secondary);
    }
    const inner = c + r + 3;
    ctx.strokeStyle = secondary; ctx.lineWidth = 0.6; ctx.globalAlpha = 0.6;
    ctx.strokeRect(inner, inner, W - inner * 2, H - inner * 2);
    ctx.globalAlpha = 1;
};

/**
 * MICROPRINT_FRAME (GreenFi specimen, 2026-11-04): THE WRITE-UP
 * IS THE BORDER. On the specimen the frame is the pale FSC
 * microprint sentence itself, repeated around all four edges in
 * a pale green margin band. There is NO drawn border line AT
 * ALL — the owner ruled it himself (2026-11-04): "the main one
 * doesn't have that borderline"; the write-up + light green
 * band ARE the edge. There is NO strong rule —
 * the earlier double-rule construction (full-alpha strokes at
 * width 1 + 0.5) was the rejected first build's frame; the
 * greenfiRebuild pin holds this construction. The pale band wash
 * under the text is the specimen's printed margin tint.
 */
export const MICROPRINT_FRAME_TEXT = 'OVER 90% OF THIS PAPER IS FROM FSC-CERTIFIED FORESTS';
const drawMicroprintFrame = (ctx: Ctx, W: number, H: number, cfg: CanvasBorderConfig) => {
    const color = cfg.color;
    // The specimen's pale margin band: a whisper of green wash
    // carrying the writing, fading at the hairline.
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.14;
    ctx.fillRect(0, 0, W, 15);
    ctx.fillRect(0, H - 15, W, 15);
    ctx.fillRect(0, 0, 15, H);
    ctx.fillRect(W - 15, 0, 15, H);
    ctx.globalAlpha = 1;
    // NO drawn line: the write-up itself is the edge treatment
    // (owner ruling, 2026-11-04). A user who wants a rule picks
    // a different border style from the picker; this style draws
    // none, exactly like the specimen.
    ctx.fillStyle = color;
    ctx.font = '5px Arial';
    ctx.textBaseline = 'middle';
    const unit = MICROPRINT_FRAME_TEXT + '   ';
    const tw = Math.max(60, typeof ctx.measureText === 'function' ? ctx.measureText(unit).width : unit.length * 3);
    for (let x = 16; x < W - 16; x += tw) {
        ctx.fillText(unit, x, 4.5);
        ctx.fillText(unit, x, H - 4.5);
    }
    ctx.save();
    ctx.translate(4.5, H - 16);
    ctx.rotate(-Math.PI / 2);
    for (let x = 0; x < H - 32; x += tw) ctx.fillText(unit, x, 0);
    ctx.restore();
    ctx.save();
    ctx.translate(W - 4.5, 16);
    ctx.rotate(Math.PI / 2);
    for (let x = 0; x < H - 32; x += tw) ctx.fillText(unit, x, 0);
    ctx.restore();
};

/**
 * DOUBLE_RULE_BOLD (USAA specimen, 2026-11-04): the design's
 * signature - heavy navy double rules across the TOP and BOTTOM
 * edges only, edge to edge. The bottom pair sits above the MICR
 * caption row, as on the specimen.
 */
const drawDoubleRuleBold = (ctx: Ctx, W: number, H: number, cfg: CanvasBorderConfig) => {
    ctx.fillStyle = cfg.color;
    ctx.fillRect(0, 3, W, 3);
    ctx.fillRect(0, 8.5, W, 1.2);
    ctx.fillRect(0, H - 24, W, 3);
    ctx.fillRect(0, H - 18.5, W, 1.2);
};

const DRAWERS: Record<NewBorderStyle, (ctx: Ctx, W: number, H: number, cfg: CanvasBorderConfig) => void> = {
    ROPE: drawRope,
    DOUBLE_RULE: drawDoubleRule,
    GREEK_KEY: drawGreekKey,
    DIAMOND_CHAIN: drawDiamondChain,
    BEAD: drawBead,
    MICROPRINT_FRAME: drawMicroprintFrame,
    DOUBLE_RULE_BOLD: drawDoubleRuleBold
};

/**
 * Draw one of the new border styles onto a 2D canvas context at
 * full check size. Returns false for styles this module does not
 * own (the pre-existing styles keep their established path).
 */
export function drawCanvasBorder(ctx: Ctx, cfg: CanvasBorderConfig, width: number, height: number): boolean {
    if (!isNewBorderStyle(cfg.style)) return false;
    ctx.save();
    DRAWERS[cfg.style](ctx, width, height, { ...cfg, secondaryColor: cfg.secondaryColor || cfg.color });
    ctx.restore();
    return true;
}
